import React, { useState, useEffect } from 'react';
import { useRoutes, useNavigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Box } from '@mui/material';
import SplashScreen from './views/authentication/SplashScreen';
import Router from './routes/Router';
import { baselightTheme, baseDarkTheme } from "./theme/DefaultColors";
// import { getAuth } from 'firebase/auth';
// import { app } from './services/firebase-config';
// import { logSiteVisit } from './services/siteVisits';
import { Analytics } from "@vercel/analytics/react";
import TermsConditionsDialog from './views/utilities/TermsConditions';
import { UserProvider } from './views/contexts/UserProvider';
import 'src/assets/images/styles/zosastyle.css';
import Seo from './services/Seo';
import HelpMenu from 'src/views/dashboard/components/zosa-components/HelpMenu';
import PromoHeader from 'src/views/authentication/PromoHeader';
import { useGlobalConfig } from './hooks/useGlobalConfig';

import { getAuth, onAuthStateChanged } from 'firebase/auth'; // Ensure these are imported
import { getFirestore, doc, onSnapshot } from 'firebase/firestore'; // Import Firestore SDK
import { app } from './services/firebase-config';
import { dismissGlobalSafetyAlert } from './services/api';
import GlobalSafetyPopup from 'src/views/zosa-components/GlobalSafetyPopup';

// Utils
import ScrollToTopOnMobile from 'src/views/zosa/zosa-app-extensions/ScrollToTopOnMobile';


const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
const isBot = /(googlebot|bingbot|crawler|spider|robot|crawling|mediapartners-google|googlebot-smartphone|google favicon)/i.test(userAgent);

function App() {
  const routing = useRoutes(Router);
  const location = useLocation(); 
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === null || savedMode === 'true';
  });

  const { showPromo, showMaintenance } = useGlobalConfig();
  const [showPromoHeader, setShowPromoHeader] = useState(showPromo);

  const [showGlobalSafetyPopup, setShowGlobalSafetyPopup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  localStorage.setItem('splashShown', 'true');

  useEffect(() => {
    if (localStorage.getItem('darkMode') === null) {
      localStorage.setItem('darkMode', 'true');
    }
  }, []);
 

  //Do not show splash for anyone
  const [showSplash, setShowSplash] = useState(false);

  // const [showTerms, setShowTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(() => {
    if (isBot) {
      return false;
    }
    // Don't show terms when user is verifying their email
    const params = new URLSearchParams(window.location.search);
    if (params.get('oobCode')) {
      return false;
    }
    return !localStorage.getItem('termsAccepted');
  });
  const navigate = useNavigate();

  const theme = isDarkMode ? baseDarkTheme : baselightTheme;


  // --- Global Safety Listener ---
  useEffect(() => {
    const auth = getAuth(app);
    const db = getFirestore(app);
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }
      if(currentUser){}
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        
        unsubscribeSnapshot = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            
            if (data.pendingSafetyAlert) {
              setShowGlobalSafetyPopup(true);
            }
          }
        }, (error) => {

        });
      }
    });
    return () => {
      if (unsubscribeSnapshot) unsubscribeSnapshot();
      unsubscribeAuth();
    };
  }, []);

  const handleGlobalSafetyDismiss = async (actionType) => {
    // 1. Close UI immediately
    setShowGlobalSafetyPopup(false);
    
    try {
        await dismissGlobalSafetyAlert(actionType); 
    } catch (error) {
        // console.error("Failed to dismiss alert on server", error);
    }
  };

  //Show splash screen to non crawlers
  useEffect(() => {

    setShowPromoHeader(showPromo);
    //if undergowing maintenance
    if (showMaintenance){
      setShowPromoHeader(false);
      navigate('/auth/maintenance'); return;
    }

    //if url contains /auth/ hide promo
    if (window.location.href.includes('/auth/') || window.location.href.includes('/getting-started')){
      setShowPromoHeader(false);
    }

    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get('oobCode');
    const fromResetPassword = sessionStorage.getItem('fromResetPassword');
    

    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = /(googlebot|bingbot|crawler|spider|robot|crawling|mediapartners-google|googlebot-smartphone|google favicon)/i.test(userAgent);

    if (isBot) {
      //Skip splash and terms for bots
      setShowSplash(false);
      setShowTerms(false);
      localStorage.setItem('termsAccepted', 'true');
    }
    
    
    if (showSplash) {
      localStorage.setItem('splashShown', 'true');
      const timer = setTimeout(() => {
        setShowSplash(false);
        if (!localStorage.getItem('termsAccepted') && !oobCode) {
          setShowTerms(true);
        }
      }, 6050);
      return () => clearTimeout(timer);
    } else {
      if (!localStorage.getItem('termsAccepted') && !oobCode) {
        setShowTerms(true);
      }
    }

    if (!oobCode && !fromResetPassword) {
      const isLoggedIn = sessionStorage.getItem('isLoggedIn');
      if (!isLoggedIn && isLoggedIn !== 'true' && isLoggedIn !== null) {
        sessionStorage.setItem('isLoggedIn', 'true');
        navigate('/', { replace: true });
      }
    }

    if (fromResetPassword) {
      sessionStorage.removeItem('fromResetPassword');
    }

    //if onboarding
    if(localStorage.getItem('zNW_ob') && !oobCode) {
      setShowPromoHeader(false);
      navigate('/getting-started');
    }

  }, [showSplash, navigate, showMaintenance, showPromo, showPromoHeader]);


  useEffect(() => {
    // Scroll to top whenever the route changes
    window.scrollTo(0, 0);
  }, [location, routing]); 


  
  // Add this effect to listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const savedMode = localStorage.getItem('darkMode') === 'true';
      setIsDarkMode(savedMode);
      document.body.classList.toggle('dark', savedMode);
    };
  
    // Listen for storage events from other tabs or manual localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom storage events from same page
    window.addEventListener('storage', handleStorageChange);
  
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Keep your existing effect for syncing body class
  useEffect(() => {
    document.body.classList.toggle('dark', isDarkMode);
    localStorage.setItem('darkMode', isDarkMode.toString());
  }, [isDarkMode]);

  useEffect(() => {
    // logSiteVisit(auth.currentUser ? auth.currentUser.uid : null);
  }, []);

  const handleTermsClose = () => {
    localStorage.setItem('termsAccepted', 'true');
    setShowTerms(false);
  };

  if (process.env.NODE_ENV !== 'development') {
    console.log = console.warn = console.error = () => {};
  }
  
  

  const clearStorage = () => {
    sessionStorage.clear();
    localStorage.clear();
    localStorage.setItem('splashShown', 'true');
    localStorage.setItem('termsAccepted', 'true');
    localStorage.setItem('darkMode', 'true');
    alert('Session and Local Storage cleared!');
    window.location.replace(window.location.origin);
  };

  const pageMeta = {
    '/': {
      title: "ZOSA | There's Always Another Layer to Discover",
      description: "Getting through it, or getting better at it. ZOSA turns your conversations into clear patterns and insights, so you understand yourself and what's next.",
      keywords: 'ZOSA, ZOSA AI, AI Therapy, ZOSA App, AI therapist, Therapy Chatbot, AI mental health companion, virtual therapist, Anonymous therapy, Anonymous AI Chatbot, Anonymous AI therapy, AI mental health, personalised therapy, mental health support, online therapy, encrypted therapy, secure mental health platform, ZOSA, mental well-being',
      canonical: 'https://zosa.app/'
    },
    '/our-purpose': {
      title: 'Our Purpose | ZOSA',
      description: 'Learn about ZOSA’s mission to make mental health support accessible, secure, and guided by AI-informed insights.',
      keywords: 'ZOSA, mission, mental health support, accessible therapy, AI mental health, mental well-being',
      canonical: 'https://zosa.app/our-purpose'
    },
    '/important-info': {
      title: 'Important Info | ZOSA',
      description: 'Discover essential details about ZOSA’s platform, including usage guidelines, privacy practices, and our commitment to user well-being.',
      keywords: 'ZOSA, important info, mental health guidelines, therapy chatbot, secure platform',
      canonical: 'https://zosa.app/important-info'
    },
    '/privacy': {
      title: 'Privacy Policy & Terms of Service | ZOSA',
      description: 'Read how ZOSA collects, encrypts, and stores your data, along with the terms that govern your use of the platform.',
      keywords: 'ZOSA privacy policy, ZOSA terms of service, mental health data protection, encrypted therapy data, AI therapy privacy',
      canonical: 'https://zosa.app/privacy'
    },
    // Serves the same document as /privacy, so it consolidates onto that canonical
    '/terms': {
      title: 'Privacy Policy & Terms of Service | ZOSA',
      description: 'Read how ZOSA collects, encrypts, and stores your data, along with the terms that govern your use of the platform.',
      keywords: 'ZOSA privacy policy, ZOSA terms of service, mental health data protection, encrypted therapy data, AI therapy privacy',
      canonical: 'https://zosa.app/privacy'
    },
    '/welcome': {
      title: 'Welcome | ZOSA',
      description: 'Get started with ZOSA and explore AI-driven mental health support. Begin your journey toward improved emotional well-being today.',
      keywords: 'ZOSA, welcome, mental health support, AI therapy introduction, start here',
      canonical: 'https://zosa.app/welcome',
      noindex: true
    },
    '/new-session': {
      title: 'New Session | ZOSA',
      description: 'Start a new session with ZOSA. Track your progress, set goals, and experience AI-informed assistance for your mental health journey.',
      keywords: 'ZOSA, new session, mental health session, AI therapy, track progress',
      canonical: 'https://zosa.app/new-session',
      noindex: true
    },
    '/journey': {
      title: 'Your Journey | ZOSA',
      description: 'Follow your mental health journey with ZOSA. Reflect on past sessions, plan future goals, and stay committed to your emotional well-being.',
      keywords: 'ZOSA, mental health journey, therapy chatbot, personalised therapy, AI therapy',
      canonical: 'https://zosa.app/journey',
      noindex: true
    },
    '/logout': {
      title: 'Logout | ZOSA',
      description: 'You have securely logged out of ZOSA. We look forward to supporting your mental well-being again soon.',
      keywords: 'ZOSA, logout, secure session, mental health platform',
      canonical: 'https://zosa.app/logout',
      noindex: true
    },
    '/account': {
      title: 'Your Account | ZOSA',
      description: 'Manage your ZOSA account settings and preferences. Keep your mental health journey private, secure, and tailored to your needs.',
      keywords: 'ZOSA, account settings, secure therapy platform, AI mental health companion',
      canonical: 'https://zosa.app/account',
      noindex: true
    },
    '/settings': {
      title: 'Settings | ZOSA',
      description: 'Adjust your ZOSA experience. Manage notifications, dark mode, and other settings to personalise your mental health journey.',
      keywords: 'ZOSA, settings, dark mode, therapy chatbot, AI therapy preferences',
      canonical: 'https://zosa.app/settings',
      noindex: true
    },
    '/crisis': {
      title: 'Crisis Resources | ZOSA',
      description: 'Access immediate help and crisis resources. While ZOSA provides supportive tools, professional intervention is crucial in emergencies.',
      keywords: 'ZOSA, crisis, emergency help, mental health support, therapy resources',
      canonical: 'https://zosa.app/crisis'
    },
    '/zosecure': {
      title: 'ZoSecure - How We Protect Your Data | ZOSA',
      description: 'Learn how ZOSA safeguards your confidential information with end-to-end encryption and secure storage.',
      keywords: 'ZOSA security, end-to-end encryption, secure therapy chat, AI privacy, ZoSecure',
      canonical: 'https://zosa.app/zosecure'
    },
    '/programs': {
      title: 'Programs | ZOSA',
      description: 'Explore ZOSA’s programs for stress management, mindfulness, and personal growth. Find the right path to enhance your mental wellness.',
      keywords: 'ZOSA, programs, mental health, stress management, mindfulness, AI therapy',
      canonical: 'https://zosa.app/programs'
    },
    '/contact': {
      title: 'Contact Us | ZOSA',
      description: 'Reach out to ZOSA for inquiries, support, or feedback. We value your input and are here to assist in your mental health journey.',
      keywords: 'ZOSA, contact, customer support, therapy chatbot, mental health assistance',
      canonical: 'https://zosa.app/contact'
    },
    '/zoki': {
      title: 'Zoki, Your ZOSA Companion Pet | ZOSA',
      description: 'Meet Zoki, a fun, interactive companion that brings a touch of lighthearted engagement to your ZOSA experience.',
      keywords: 'ZOSA, Zoki, virtual companion, mental health support, fun therapy',
      canonical: 'https://zosa.app/zoki'
    },
    '/talk': {
      title: 'Talk Session | ZOSA',
      description: 'Initiate a talk session with our voice-based or text-based AI features. Share, reflect, and find supportive guidance in real time.',
      keywords: 'ZOSA, talk session, voice mode, AI mental health companion, therapy chatbot',
      canonical: 'https://zosa.app/talk',
      noindex: true
    },
    '/membership': {
      title: 'ZOSA Membership - Unlock Exclusive Benefits',
      description: 'Upgrade to ZOSA Membership and access exclusive features like AI-guided sessions, priority support, and advanced analytics.',
      keywords: 'ZOSA membership, AI therapy subscription, mental health premium features',
      canonical: 'https://zosa.app/membership'
    },
    '/explore': {
      title: 'Deep Dives, Guided Sessions on Specific Topics | ZOSA',
      description: 'Dive deeper into resources and self-help tools curated by ZOSA. Find articles, guides, and tips for enhanced mental well-being.',
      keywords: 'ZOSA, explore, mental health resources, therapy tips, AI therapy guides',
      canonical: 'https://zosa.app/explore'
    },
    '/appointments': {
      title: 'Appointments | ZOSA',
      description: 'Manage and schedule appointments for specialised sessions. Stay organised and committed to your mental health goals with ZOSA.',
      keywords: 'ZOSA, appointments, mental health schedule, AI therapy sessions, personal growth',
      canonical: 'https://zosa.app/appointments'
    },

    '/faqs': {
      title: 'Frequently Asked Questions About ZOSA | ZOSA',
      description: 'Find answers to frequently asked questions about ZOSA’s AI-driven mental health companion and the various features we offer.',
      keywords: 'ZOSA FAQs, therapy chatbot questions, AI therapy platform, frequently asked questions',
      canonical: 'https://zosa.app/faqs'
    },

    '/analysis': {
      title: 'Character Analysis | ZOSA',
      description: 'Unlock self-understanding with AI-powered analysis of your ZOSA sessions, surfacing traits, patterns and growth ideas.',
      keywords: 'ZOSA, character analysis, AI-powered insights, mental health support, session review, growth suggestions, virtual therapist, therapy chatbot, ZOSA analysis',
      canonical: 'https://zosa.app/analysis'
    },

    '/getting-started': {
      title: 'Getting Started | ZOSA',
      description: 'Begin your journey with ZOSA in a few steps: create your account, set up your companion, and find the tools that suit your mental well-being.',
      keywords: 'ZOSA onboarding, getting started guide, AI therapy setup, mental health companion, ZOSA account setup, first steps',
      canonical: 'https://zosa.app/getting-started'
    },

    '/community': {
      title: 'Community | ZOSA',
      description: 'Join our supportive community of ZOSA users, where you can test community prompts and find inspiration for your mental health journey.',
      keywords: 'ZOSA community, mental health support, AI therapy reddit, virtual therapist, ZOSA users, community support',
      canonical: 'https://zosa.app/community'
    },

    '/safety': {
      title: 'Safety and Crisis Support at ZOSA | ZOSA',
      description: 'Learn about ZOSA’s commitment to safety and security, including our data practices, encryption, and privacy policies.',
      keywords: 'ZOSA safety, mental health support, therapy chatbot, virtual therapist, privacy policy, data protection',
      canonical: 'https://zosa.app/safety'
    },

    '/zosa-at-work': {
      title: 'ZOSA For Business - Mental Health Support for Modern Teams',
      description: 'Empower your workforce with immediate, anonymous, and clinically architected mental health support. Secure, scalable, and designed for high-performance teams.',
      keywords: 'employee mental health, corporate wellness, EAP alternative, AI mental health for business, workplace wellbeing, anonymous employee support',
      canonical: 'https://zosa.app/zosa-at-work'
    },

    '/notes': {
      title: 'Session Notes | ZOSA',
      description: 'Securely upload, encrypt, and manage your personal session notes in ZOSA. Add text, images, or PDFs and use them as private context for focused AI sessions.',
      keywords: 'ZOSA notes, encrypted therapy notes, AI therapy notes, private mental health notes, ZoSecure encryption, session notes',
      canonical: 'https://zosa.app/notes',
      noindex: true
    },

    '/tools': {
      title: 'Tools & Techniques | ZOSA',
      description: 'Save and organise personalised mental health tools, coping strategies, and techniques from your ZOSA sessions for quick access whenever you need them.',
      keywords: 'ZOSA tools, mental health tools, coping techniques, CBT tools, mindfulness exercises, therapy techniques',
      canonical: 'https://zosa.app/tools',
      noindex: true
    },

    '/mind-graph': {
      title: 'MindGraph | ZOSA',
      description: 'Visualise the connections behind your wellbeing. MindGraph maps your patterns to help you find root causes and track progress over time.',
      keywords: 'ZOSA MindGraph, mental health symptom tracker, visual therapy map, symptom network, AI mental health mapping, root cause analysis, therapy progress tracking',
      canonical: 'https://zosa.app/mind-graph'
    },
    '/nd-info': {
      title: 'ZOSA for Neurodivergent Minds | ZOSA',
      description: 'How ZOSA adapts for autistic, ADHD and neurodivergent users. Clearer language, special interest anchors, no masking required.',
      keywords: 'ZOSA neurodivergent, ADHD support app, autistic AI companion, neurodivergent mental health, special interests, no masking, literal language AI',
      canonical: 'https://zosa.app/nd-info'
    },
    '/understanding-zosa': {
      title: 'Understanding ZOSA, How It Works | ZOSA',
      description: 'A clear look at how ZOSA works and the science behind it: the network model of the mind, real-time state matching, memory, style, and an anonymous safety layer.',
      keywords: 'how ZOSA works, science behind ZOSA, AI mental health science, network theory of mental health, MindGraph, therapeutic alliance AI, AI long-term memory, predictive brain, AI therapy science, anonymous mental health AI',
      canonical: 'https://zosa.app/understanding-zosa'
    },


    // Private, transactional and auth pages.
    // These are kept out of the sitemap and marked noindex so they stop
    // inheriting the homepage title and canonical.
    '/delete-account': {
      title: 'Delete Your Account | ZOSA',
      description: 'Permanently delete your ZOSA account and the data associated with it.',
      canonical: 'https://zosa.app/delete-account',
      noindex: true
    },
    '/business-insights': {
      title: 'Business Insights | ZOSA',
      description: 'Anonymised wellbeing insights for ZOSA for Business account holders.',
      canonical: 'https://zosa.app/business-insights',
      noindex: true
    },
    '/partners': {
      title: 'Partner Dashboard | ZOSA',
      description: 'Manage your ZOSA partner account and view your organisation’s dashboard.',
      canonical: 'https://zosa.app/partners',
      noindex: true
    },
    '/admin': {
      title: 'Admin | ZOSA',
      description: 'Internal ZOSA administration.',
      canonical: 'https://zosa.app/admin',
      noindex: true
    },
    '/pathway': {
      title: 'Pathway | ZOSA',
      description: 'Set up your personalised ZOSA pathway.',
      canonical: 'https://zosa.app/pathway',
      noindex: true
    },
    '/pathway/review': {
      title: 'Pathway Review | ZOSA',
      description: 'Review the progress you have made along your ZOSA pathway.',
      canonical: 'https://zosa.app/pathway/review',
      noindex: true
    },
    '/seed': {
      title: 'Recovery Phrase | ZOSA',
      description: 'Generate and store the recovery phrase that protects your encrypted ZOSA data.',
      canonical: 'https://zosa.app/seed',
      noindex: true
    },
    '/verify-email': {
      title: 'Verify Your Email | ZOSA',
      description: 'Confirm your email address to finish setting up your ZOSA account.',
      canonical: 'https://zosa.app/verify-email',
      noindex: true
    },
    '/auth/login': {
      title: 'Log In | ZOSA',
      description: 'Log in to your ZOSA account.',
      canonical: 'https://zosa.app/auth/login',
      noindex: true
    },
    '/auth/register': {
      title: 'Create an Account | ZOSA',
      description: 'Create a ZOSA account to begin your mental health journey.',
      canonical: 'https://zosa.app/auth/register',
      noindex: true
    },
    '/auth/forgot-password': {
      title: 'Forgotten Password | ZOSA',
      description: 'Reset the password for your ZOSA account.',
      canonical: 'https://zosa.app/auth/forgot-password',
      noindex: true
    },
    '/auth/reset-password': {
      title: 'Reset Password | ZOSA',
      description: 'Choose a new password for your ZOSA account.',
      canonical: 'https://zosa.app/auth/reset-password',
      noindex: true
    },
    '/auth/confirm': {
      title: 'Confirm Action | ZOSA',
      description: 'Confirm the requested change to your ZOSA account.',
      canonical: 'https://zosa.app/auth/confirm',
      noindex: true
    },
    '/auth/404': {
      title: 'Page Not Found | ZOSA',
      description: 'The page you were looking for could not be found.',
      canonical: 'https://zosa.app/auth/404',
      noindex: true
    },
    '/auth/maintenance': {
      title: 'Under Maintenance | ZOSA',
      description: 'ZOSA is briefly unavailable while we carry out maintenance. Please check back shortly.',
      canonical: 'https://zosa.app/auth/maintenance',
      noindex: true
    },


    // Keyword-based SEO Pages
    '/zosa': {
      title: 'ZOSA | What It Is and How It Works',
      description: 'What ZOSA is, what it does with your conversations, and what it is not. Built to show your repeating patterns, not to replace professional care.',
      keywords: 'ZOSA AI, AI therapy, mental health companion, therapy chatbot, virtual therapist',
      canonical: 'https://zosa.app/zosa'
    },
    '/zosa-ai': {
      title: 'ZOSA AI, the Engine Behind Your Sessions | ZOSA',
      description: 'The engine underneath ZOSA: how conversations become patterns, which psychological frameworks it draws on, and what it is not.',
      keywords: 'ZOSA AI, AI therapy, mental health companion, therapy chatbot, virtual therapist',
      canonical: 'https://zosa.app/zosa-ai'
    },
    '/ai-therapy': {
      title: 'AI Therapy | ZOSA',
      description: 'What AI therapy tools do, what they cannot do, and the questions worth asking before you use one. ZOSA is not therapy and does not replace it.',
      keywords: 'AI therapy, ZOSA, mental health support, therapy chatbot, AI mental health companion',
      canonical: 'https://zosa.app/ai-therapy'
    },
    '/zosa-app': {
      title: 'ZOSA App | ZOSA',
      description: 'What the ZOSA app does: text and voice sessions, notes, tracking over time, and a picture of the patterns you have been repeating.',
      keywords: 'ZOSA App, AI therapist, therapy chatbot, online therapy, mental well-being',
      canonical: 'https://zosa.app/zosa-app'
    },
    '/ai-therapist': {
      title: 'AI Therapist | ZOSA',
      description: 'What the term AI therapist actually means, where the comparison to a real one breaks down, and what to expect. ZOSA is not a licensed professional.',
      keywords: 'AI therapist, ZOSA, virtual therapist, mental health support, therapy chatbot',
      canonical: 'https://zosa.app/ai-therapist'
    },
    '/therapy-chatbot': {
      title: 'Therapy Chatbot | ZOSA',
      description: 'What a therapy chatbot helps with, where it falls short, and the one difference worth testing before you pick one. ZOSA is not a therapist.',
      keywords: 'therapy chatbot, ZOSA, AI therapy, anonymous therapy, mental health companion',
      canonical: 'https://zosa.app/therapy-chatbot'
    },
    '/ai-mental-health-companion': {
      title: 'AI Mental Health Companion | ZOSA',
      description: 'What a mental health companion is actually for: the hours between appointments, and the patterns nobody else is keeping track of.',
      keywords: 'AI mental health companion, ZOSA, therapy chatbot, mental health support',
      canonical: 'https://zosa.app/ai-mental-health-companion'
    },
    '/virtual-therapist': {
      title: 'Virtual Therapist | ZOSA',
      description: 'Most of a difficult week happens nowhere near an appointment. How ZOSA supports the hours in between, and why it is not a licensed practitioner.',
      keywords: 'virtual therapist, ZOSA, AI therapy, online therapy, mental health platform',
      canonical: 'https://zosa.app/virtual-therapist'
    },
    '/anonymous-therapy': {
      title: 'Anonymous Therapy | ZOSA',
      description: 'The fear of being judged stops more people than the cost does. How anonymous support works with ZOSA, what stays private, and where it is not enough.',
      keywords: 'anonymous therapy, ZOSA, AI therapist, secure mental health, therapy chatbot',
      canonical: 'https://zosa.app/anonymous-therapy'
    },
    '/anonymous-ai-chatbot': {
      title: 'Anonymous AI Chatbot | ZOSA',
      description: 'Some things are easier to say when nobody knows it is you. How anonymous conversations work with ZOSA, what stays encrypted, and when to speak to a person.',
      keywords: 'anonymous AI chatbot, ZOSA, virtual therapist, mental health support, online therapy',
      canonical: 'https://zosa.app/anonymous-ai-chatbot'
    },
    '/anonymous-ai-therapy': {
      title: 'Anonymous AI Therapy | ZOSA',
      description: 'Discreet support without giving your name. How anonymous conversations work with ZOSA, what stays encrypted, and when to speak to a qualified person.',
      keywords: 'anonymous AI therapy, ZOSA, therapy chatbot, mental health companion, secure platform',
      canonical: 'https://zosa.app/anonymous-ai-therapy'
    },
    '/ai-mental-health': {
      title: 'AI Mental Health | ZOSA',
      description: 'Your own memory is the least reliable record you have. How AI tools track emotions and stressors over time, and where they stop being enough.',
      keywords: 'AI mental health, ZOSA, therapy chatbot, mental well-being, online therapy',
      canonical: 'https://zosa.app/ai-mental-health'
    },
    '/personalised-therapy': {
      title: 'Personalised Therapy | ZOSA',
      description: 'Generic advice fails because it does not know what you have already tried. How ZOSA works from your own history, and where it is not the right answer.',
      keywords: 'personalised therapy, ZOSA, AI mental health, virtual therapist, therapy chatbot',
      canonical: 'https://zosa.app/personalised-therapy'
    },
    '/mental-health-support': {
      title: 'Mental Health Support | ZOSA',
      description: 'You do not have to be in crisis to want support. What ZOSA offers, what it costs, and where professional help is the better answer.',
      keywords: 'mental health support, ZOSA, AI therapy, therapy chatbot, secure mental health platform',
      canonical: 'https://zosa.app/mental-health-support'
    },
    '/online-therapy': {
      title: 'Online Therapy | ZOSA',
      description: 'Online therapy removed the travel, not the waiting list or the cost. What ZOSA does for the gap in between, and why it is not therapy.',
      keywords: 'online therapy, ZOSA, AI therapist, mental health support, therapy chatbot',
      canonical: 'https://zosa.app/online-therapy'
    },
    '/encrypted-therapy': {
      title: 'Encrypted Therapy | ZOSA',
      description: 'Honesty is the thing that makes any of this work, and it is hard when you are unsure who can read it. How ZOSA encrypts sessions, and what that means.',
      keywords: 'encrypted therapy, ZOSA, secure mental health, AI privacy, therapy chatbot',
      canonical: 'https://zosa.app/encrypted-therapy'
    },
    '/secure-mental-health-platform': {
      title: 'Secure Mental Health Platform | ZOSA',
      description: 'What actually makes a mental health platform secure, the questions worth asking any of them, and how ZOSA handles your sessions.',
      keywords: 'secure mental health platform, ZOSA, AI therapy, encrypted therapy, therapy chatbot',
      canonical: 'https://zosa.app/secure-mental-health-platform'
    },
    '/mental-well-being': {
      title: 'Mental Well-Being | ZOSA',
      description: 'Wellbeing is easier to work on when you can see it. How ZOSA tracks what changes over time, and where professional support is still needed.',
      keywords: 'mental well-being, ZOSA, AI therapy, therapy chatbot, mental health support',
      canonical: 'https://zosa.app/mental-well-being'
    },
    '/free-ai-therapy-chatbot': {
      title: 'Free AI Therapy Chatbot | ZOSA',
      description: 'Cost is the barrier that stops most people. What a free AI therapy chatbot can and cannot do, and what free is usually paying for.',
      keywords: 'Free AI therapy chatbot, ZOSA, AI mental health, therapy chatbot, accessible mental wellness',
      canonical: 'https://zosa.app/free-ai-therapy-chatbot'
    },
    '/best-free-ai-therapy-bots': {
      title: 'Best Free AI Therapy Bots | ZOSA',
      description: 'Free AI therapy bots compared: what free actually buys you, what to check before trusting one, and where a qualified professional is still needed.',
      keywords: 'Best free AI therapy bots, ZOSA, mental health support, therapy chatbot, AI therapy',
      canonical: 'https://zosa.app/best-free-ai-therapy-bots'
    },
    '/explore-free-ai-therapy-apps': {
      title: 'Explore Free AI Therapy Apps | ZOSA',
      description: 'Trying free AI therapy apps? What they are good for, what to check before you commit, and where they stop being enough.',
      keywords: 'Explore free AI therapy apps, ZOSA, mental health resources, therapy chatbot',
      canonical: 'https://zosa.app/explore-free-ai-therapy-apps'
    },
    '/pros-and-cons-of-ai-therapy': {
      title: 'Pros and Cons of AI Therapy | ZOSA',
      description: 'The real trade-offs of AI therapy tools: what they are good at, what they cannot do, and where a human is not optional.',
      keywords: 'Pros and cons of AI therapy, ZOSA, AI mental health, therapy bot pros and cons',
      canonical: 'https://zosa.app/pros-and-cons-of-ai-therapy'
    },
    '/is-ai-therapy-helpful': {
      title: 'Is AI Therapy Helpful? | ZOSA',
      description: 'An honest look at what AI therapy tools help with, what they do not, and the one thing worth checking before you rely on one.',
      keywords: 'Is AI therapy helpful, ZOSA, AI therapist, mental health chatbot, therapy efficacy',
      canonical: 'https://zosa.app/is-ai-therapy-helpful'
    },
    '/ai-therapy-apps': {
      title: 'AI Therapy Apps | ZOSA',
      description: 'What AI therapy apps do, how they differ from one another, and the questions worth asking before you rely on one. ZOSA is not a therapy provider.',
      keywords: 'AI therapy apps, ZOSA, online therapy, therapy chatbot, mental health technology',
      canonical: 'https://zosa.app/ai-therapy-apps'
    },
    '/using-ai-for-cbt': {
      title: 'Using AI for CBT | ZOSA',
      description: 'CBT works by catching a thought in the moment, which rarely happens during an appointment. How AI tools help in between, and what they cannot do.',
      keywords: 'Using AI for CBT, ZOSA, cognitive behavioural therapy, mental health chatbot, therapy AI',
      canonical: 'https://zosa.app/using-ai-for-cbt'
    },
    '/using-ai-for-cbt-and-dbt': {
      title: 'Using AI for CBT and DBT | ZOSA',
      description: 'CBT reframes the thought, DBT handles the feeling underneath it. How AI tools support both between sessions, and where a professional is needed.',
      keywords: 'Using AI for CBT and DBT, ZOSA, dialectical behaviour therapy, cognitive behavioural therapy, therapy chatbot',
      canonical: 'https://zosa.app/using-ai-for-cbt-and-dbt'
    },
    '/which-ai-chatbot-is-best': {
      title: 'Which AI Chatbot Is Best? | ZOSA',
      description: 'It depends what you want one for. The honest way to compare mental health chatbots, and the single difference most people overlook.',
      keywords: 'Which AI chatbot is best, ZOSA, mental health, therapy chatbot comparison, AI therapy review',
      canonical: 'https://zosa.app/which-ai-chatbot-is-best'
    },
    '/top-ai-chatbots-for-mental-health': {
      title: 'Top AI Chatbots for Mental Health | ZOSA',
      description: 'How to compare mental health chatbots when they all describe themselves the same way. The differences that actually matter, and how to check them.',
      keywords: 'Top AI chatbots for mental health, ZOSA, AI therapy guide, therapy chatbot, online mental health',
      canonical: 'https://zosa.app/top-ai-chatbots-for-mental-health'
    },
    '/what-is-an-ai-therapist': {
      title: 'What Is an AI Therapist? | ZOSA',
      description: 'What the term means, what these tools actually do, and where the comparison to a qualified therapist stops being accurate.',
      keywords: 'What is an AI therapist, ZOSA, AI therapy, mental health chatbot, virtual therapist explained',
      canonical: 'https://zosa.app/what-is-an-ai-therapist'
    },
    '/overcoming-stigma-ai-mental-health': {
      title: 'Overcoming Stigma: AI and Mental Health | ZOSA',
      description: 'Fear of being judged stops more people than cost does. How semi-anonymous tools change who asks for help, and what that does and does not solve.',
      keywords: 'Overcoming stigma AI mental health, ZOSA, therapy chatbot, normalising mental healthcare, AI mental health companion',
      canonical: 'https://zosa.app/overcoming-stigma-ai-mental-health'
    },
    '/the-science-behind-ai-therapy': {
      title: 'The Science Behind AI Therapy: Can It Help? | ZOSA',
      description: 'What the research actually shows about AI mental health tools, where the evidence is thin, and which psychological frameworks sit underneath them.',
      keywords: 'The science behind AI therapy, ZOSA, mental health chatbot research, AI therapy efficacy',
      canonical: 'https://zosa.app/the-science-behind-ai-therapy'
    },
    '/best-ai-therapy': {
      title: 'Best AI Therapy | ZOSA',
      description: 'How to tell AI therapy tools apart in a crowded market: what to test, what the claims usually hide, and where a qualified person is still the answer.',
      keywords: 'best AI therapy, best AI therapist, best AI therapy app, AI mental health, therapy chatbot, ZOSA, online therapy, mental well-being',
      canonical: 'https://zosa.app/best-ai-therapy'
    },
    '/best-ai-therapist': {
      title: 'Best AI Therapist | ZOSA',
      description: 'Comparing AI therapists? The difference that matters is whether it remembers you between conversations. What to test, and where a person is the better answer.',
      keywords: 'best AI therapy, best AI therapist, best AI therapy app, ZOSA, virtual therapist, mental health chatbot, AI therapy companion',
      canonical: 'https://zosa.app/best-ai-therapist'
    },
    '/best-ai-therapy-app': {
      title: 'Best AI Therapy App | ZOSA',
      description: 'Looking for an AI therapy app? What to weigh up before you choose one: cost, memory, privacy, and where an app should not be used at all.',
      keywords: 'best AI therapy, best AI therapist, best AI therapy app, ZOSA, mental health, therapy chatbot, AI-guided therapy, online support',
      canonical: 'https://zosa.app/best-ai-therapy-app'
    },
    '/is-my-problem-serious-enough': {
      title: 'Is My Problem Serious Enough to Get Help? | ZOSA',
      description: 'If you are asking the question, that is usually the answer. Why the threshold you are imagining does not exist, and where to start.',
      keywords: 'is my problem serious enough, do I need therapy, am I overreacting, mental health support, when to get help',
      canonical: 'https://zosa.app/is-my-problem-serious-enough'
    },
    '/why-journalling-does-not-work': {
      title: 'Why Journalling Does Not Work, and What Does | ZOSA',
      description: 'You can keep a journal for a year and learn nothing from it. Why writing it down is not the same as seeing it, and what closes the gap.',
      keywords: 'why journalling does not work, journaling not helping, journal for mental health, mood tracking, self reflection',
      canonical: 'https://zosa.app/why-journalling-does-not-work'
    },
    '/chatgpt-for-mental-health': {
      title: 'Using ChatGPT for Mental Health: What It Gets Wrong | ZOSA',
      description: 'General AI is genuinely useful at 3am and genuinely limited. What it gets right, the two things it gets wrong, and when that matters.',
      keywords: 'chatgpt for mental health, chatgpt therapy, using ai for mental health, ai chatbot mental health, chatgpt vs therapy',
      canonical: 'https://zosa.app/chatgpt-for-mental-health'
    },
    '/cant-afford-therapy': {
      title: 'What to Do When You Cannot Afford Therapy | ZOSA',
      description: 'Cost is the most common reason people get no support at all. The free and low cost routes worth trying first, and what helps in the meantime.',
      keywords: 'cannot afford therapy, cheap therapy alternative, free mental health support, low cost counselling, affordable therapy',
      canonical: 'https://zosa.app/cant-afford-therapy'
    },
    '/how-to-track-your-mental-health': {
      title: 'How to Actually Track Your Mental Health | ZOSA',
      description: 'Most mood tracking throws away the part that mattered. What is actually worth recording, why a score out of ten is not it, and how to keep it up.',
      keywords: 'how to track mental health, mood tracker, mental health tracking, mood diary, track your patterns, self awareness',
      canonical: 'https://zosa.app/how-to-track-your-mental-health'
    },
    '/between-therapy-sessions': {
      title: 'Mental Health Support Between Therapy Sessions | ZOSA',
      description: 'An hour a week is one hour in 168. What to do with the other 167, and how to arrive at your next session with the pattern rather than the anecdote.',
      keywords: 'between therapy sessions, support between appointments, therapy homework, therapy prep, mental health between sessions',
      canonical: 'https://zosa.app/between-therapy-sessions'
    },
    '/why-you-cant-see-your-own-patterns': {
      title: 'Why You Cannot See Your Own Patterns | ZOSA',
      description: 'Your memory is the least reliable record you have, and it is the only one most people keep. Why self-observation fails, and what fixes it.',
      keywords: 'why you cannot see your own patterns, self awareness, recurring patterns, emotional patterns, blind spots, self reflection',
      canonical: 'https://zosa.app/why-you-cant-see-your-own-patterns'
    },
    '/ai-benefits-for-employees': {
      title: 'AI Benefits for Employees: What Actually Helps | ZOSA',
      description: 'Employers are adding AI tools to benefits packages faster than they can evaluate them. What is worth offering, and where mental health support fits.',
      keywords: 'AI benefits for employees, AI employee benefits, employee wellbeing benefits, AI mental health benefit, workplace AI tools',
      canonical: 'https://zosa.app/ai-benefits-for-employees'
    },
    '/eap-alternative': {
      title: 'EAP Alternative, Why Employers Add AI Support | ZOSA',
      description: 'Traditional Employee Assistance Programmes have a usage problem more than a quality one. Where AI support fits alongside an EAP, not replacing one.',
      keywords: 'EAP alternative, employee assistance programme alternative, AI EAP, employee assistance programme usage, workplace mental health support',
      canonical: 'https://zosa.app/eap-alternative'
    },
    '/cost-of-employee-burnout': {
      title: 'The Cost of Employee Burnout to Your Business | ZOSA',
      description: 'Burnout shows up as attrition, absence and quiet disengagement long before anyone names it. What it costs, and why prevention beats replacement.',
      keywords: 'cost of employee burnout, employee burnout, workplace burnout, staff turnover cost, presenteeism, employee attrition',
      canonical: 'https://zosa.app/cost-of-employee-burnout'
    },
    '/employee-mental-health-benefit-cost': {
      title: 'What Should a Mental Health Benefit Cost? | ZOSA',
      description: 'Traditional EAPs are priced per head whether anyone uses them. What a mental health benefit costs, what drives the price, and what to pay for.',
      keywords: 'employee mental health benefit cost, EAP cost per employee, cost of employee wellbeing benefit, corporate mental health pricing',
      canonical: 'https://zosa.app/employee-mental-health-benefit-cost'
    },
    '/how-to-choose-employee-mental-health-benefit': {
      title: 'Choosing a Mental Health Benefit Staff Will Use | ZOSA',
      description: 'Most employee benefits go unused, and mental health support is the one where that matters most. What to look for so people actually open it.',
      keywords: 'how to choose employee mental health benefit, employee wellbeing benefit checklist, choosing an EAP, employee mental health vendor',
      canonical: 'https://zosa.app/how-to-choose-employee-mental-health-benefit'
    },

    // Guides hub
    '/guides': {
      title: 'Guides | ZOSA',
      description: 'Every ZOSA guide in one place: getting started, understanding how ZOSA works, comparing AI therapy options, privacy and safety, and support for employers.',
      keywords: 'ZOSA guides, AI therapy guides, mental health guides, ZOSA help centre, AI mental health resources',
      canonical: 'https://zosa.app/guides'
    },

    // 5 new SEO pages, added alongside the guides hub
    '/zosa-vs-betterhelp-and-talkspace': {
      title: 'ZOSA vs BetterHelp and Talkspace | ZOSA',
      description: 'BetterHelp and Talkspace connect you with a human therapist through an app. ZOSA is something different: free to start, always on, and genuinely AI.',
      keywords: 'ZOSA vs BetterHelp, ZOSA vs Talkspace, AI therapy vs BetterHelp, is BetterHelp AI, online therapy comparison, AI mental health companion',
      canonical: 'https://zosa.app/zosa-vs-betterhelp-and-talkspace'
    },
    '/ai-companion-for-loneliness': {
      title: 'Can an AI Companion Help With Loneliness? | ZOSA',
      description: 'AI companions ease loneliness in the moment, but research shows heavy use can deepen it for some. What the evidence says, and how ZOSA is built differently.',
      keywords: 'AI companion for loneliness, AI and loneliness, chatbot loneliness research, AI mental health companion, virtual companion mental health',
      canonical: 'https://zosa.app/ai-companion-for-loneliness'
    },
    '/is-it-safe-to-talk-to-an-ai-about-your-mental-health': {
      title: 'Is It Safe to Tell an AI About Your Mental Health? | ZOSA',
      description: 'Most AI chatbots log what you say, and some use it to train future models. What to check before you open up, and how ZOSA answers each concern.',
      keywords: 'is AI chatbot safe for mental health, AI therapy privacy, is my data safe AI chatbot, AI chatbot confidentiality, secure mental health platform',
      canonical: 'https://zosa.app/is-it-safe-to-talk-to-an-ai-about-your-mental-health'
    },
    '/signs-of-burnout': {
      title: 'Signs of Burnout, and What Actually Helps | ZOSA',
      description: 'Burnout is easy to miss from the inside because exhaustion becomes the new normal. The signs worth taking seriously, and where to start.',
      keywords: 'signs of burnout, burnout symptoms, am I burnt out, burnout vs depression, how to recover from burnout, workplace burnout',
      canonical: 'https://zosa.app/signs-of-burnout'
    },
    '/ai-support-for-adhd-and-rsd': {
      title: 'AI Support for ADHD and Rejection Sensitive Dysphoria | ZOSA',
      description: 'Rejection sensitive dysphoria turns ordinary feedback into a crisis, fast. How ZOSA is built to help in that moment, without judgement or masking.',
      keywords: 'AI support for ADHD, rejection sensitive dysphoria, RSD ADHD, ADHD AI companion, neurodivergent mental health support',
      canonical: 'https://zosa.app/ai-support-for-adhd-and-rsd'
    },

    // Mental fitness / performance angle: 5 more SEO pages
    '/mental-fitness-tracker': {
      title: 'Mental Fitness Tracker, the Strava for Your Mind | ZOSA',
      description: 'There is no Whoop for your mind yet, not quite. What mental fitness tracking actually means, and how ZOSA does the closest thing to it today.',
      keywords: 'mental fitness tracker, Whoop for mental health, Strava for mental health, mood tracking app, mental fitness app, track mental patterns',
      canonical: 'https://zosa.app/mental-fitness-tracker'
    },
    '/mental-health-competitive-edge': {
      title: 'Improving Your Mental Health to Get an Edge at Work | ZOSA',
      description: 'In a competitive work environment, the person thinking clearest wins more often than the person working hardest. How mental fitness becomes an edge.',
      keywords: 'mental health competitive edge, mental fitness at work, performance psychology, mental edge workplace, competitive advantage mental health',
      canonical: 'https://zosa.app/mental-health-competitive-edge'
    },
    '/mental-reps-building-resilience': {
      title: 'Mental Reps, Building Resilience Like a Muscle | ZOSA',
      description: 'Nobody expects one gym session to change their body. Resilience works the same way. What a mental rep actually looks like, and how to keep doing them.',
      keywords: 'mental reps, building resilience, mental fitness training, resilience training, mental strength exercises, train your mind',
      canonical: 'https://zosa.app/mental-reps-building-resilience'
    },
    '/track-your-mental-state-like-hrv': {
      title: 'Why High Performers Track Their Mind Like Their HRV | ZOSA',
      description: 'Athletes stopped guessing how recovered they were and started measuring it. Applying the same logic to your mental state, and what to actually track.',
      keywords: 'track mental state, HRV mental health, quantified self mental health, mood data tracking, biometric mental health, mental readiness score',
      canonical: 'https://zosa.app/track-your-mental-state-like-hrv'
    },
    '/mental-health-for-high-performers': {
      title: 'Mental Health for High Performers and Founders | ZOSA',
      description: 'Driven people tend to ignore their own mental state until it shows up in their output. Why high performers are often the last to notice, and what helps.',
      keywords: 'mental health for high performers, founder mental health, executive burnout, high achiever mental health, entrepreneur mental health support',
      canonical: 'https://zosa.app/mental-health-for-high-performers'
    },

    '/auth': {
      title: 'Sign In | ZOSA',
      description: 'Sign in to ZOSA.',
      canonical: 'https://zosa.app/auth',
      noindex: true
    },
    '/b2b': {
      title: 'ZOSA for Business Portal | ZOSA',
      description: 'Portal for ZOSA for Business account holders.',
      canonical: 'https://zosa.app/b2b',
      noindex: true
    },
    '/aCr1WUq7JZch': {
      title: 'ZOSA',
      description: 'ZOSA',
      canonical: 'https://zosa.app/aCr1WUq7JZch',
      noindex: true
    },
    '/2CwfeVhT1bIh': {
      title: 'ZOSA',
      description: 'ZOSA',
      canonical: 'https://zosa.app/2CwfeVhT1bIh',
      noindex: true
    },
    '/current-progress': {
      title: 'Your Progress | ZOSA',
      description: 'Your progress inside ZOSA.',
      canonical: 'https://zosa.app/current-progress',
      noindex: true
    },
    '/feedback': {
      title: 'Feedback | ZOSA',
      description: 'Send feedback to the ZOSA team.',
      canonical: 'https://zosa.app/feedback',
      noindex: true
    },
    '/seed-phrase': {
      title: 'Access Key | ZOSA',
      description: 'Set up your ZoSecure access key.',
      canonical: 'https://zosa.app/seed-phrase',
      noindex: true
    },
    '/ui/shadow': {
      title: 'Internal | ZOSA',
      description: 'Internal design reference.',
      canonical: 'https://zosa.app/ui/shadow',
      noindex: true
    },
    '/ui/typography': {
      title: 'Internal | ZOSA',
      description: 'Internal design reference.',
      canonical: 'https://zosa.app/ui/typography',
      noindex: true
    },
    '/voidboard': {
      title: 'Void | ZOSA',
      description: 'A private space inside ZOSA.',
      canonical: 'https://zosa.app/voidboard',
      noindex: true
    }

  };
  

  // Default to home metadata if route is not explicitly defined
  const currentMeta = pageMeta[location.pathname] || pageMeta['/'];

  return (
    <UserProvider>
      <ThemeProvider theme={theme}>
      <Seo 
          title={currentMeta.title}
          description={currentMeta.description}
          keywords={currentMeta.keywords}
          canonical={currentMeta.canonical}
          noindex={currentMeta.noindex === true}
        />
        <ScrollToTopOnMobile />
        <CssBaseline />

        <GlobalSafetyPopup 
            open={showGlobalSafetyPopup} 
            onClose={handleGlobalSafetyDismiss} 
        />

        {showPromoHeader && !showSplash && !isBot && showPromo && !showMaintenance && <PromoHeader />}

        {showSplash ? <SplashScreen /> : routing}
        <TermsConditionsDialog open={showTerms && !showSplash} handleClose={handleTermsClose} />
        {process.env.NODE_ENV !== 'development' && <Analytics />}
        {process.env.NODE_ENV === 'development' && (
          <button
          hidden
            onClick={clearStorage}
            style={{
              position: 'fixed',
              top: '10px',
              right: '30%',
              backgroundColor: 'rgba(255, 0, 0, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              padding: '8px 12px',
              cursor: 'pointer',
              zIndex: 1000,
              fontSize: '12px',
            }}
          >
            DEV Wipe Session & Storage
          </button>
        )}
        <Box className="help-menu">
        <HelpMenu open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
        </Box>
      </ThemeProvider>
    </UserProvider>
  );
}

export default App;
